// App-side delegate for `@heidi/templates/data` (D2.3, ARCFE-1030).
//
// The package hook owns the save plan, the request (edit-in-place vs
// copy-to-new) and every templates cache reconciliation. What stayed here is
// what could not move:
//
//   - `useOrganizationTemplatesQuery` + `useFilters` — a `@/app-shell/settings/organization`
//     query with no package home. It supplies `onMutate`'s snapshot, so the
//     LOOKUP crosses as a port and the snapshot stays app-typed.
//   - four analytics dispatches, one of them behind a deep-equality comparison
//     of the previous and new `shared_team_ids`
//   - the success toast and the compliance-error modal chain
import {
  IS_COMPLIANCE_CHECK_CREATE_TEMPLATE_ENABLED,
  IS_COMPLIANCE_CHECK_UPDATE_TEMPLATE_ENABLED,
  IS_TEMPLATE_CREATION_RECORD_ENABLED,
} from '@/app-shell/flags/templates-flags';
import { useFilters } from '@/app-shell/settings/organization/templates-library/organization-templates-table/hooks/useFilters';
import { useOrganizationTemplatesQuery } from '@/app-shell/settings/organization/templates-library/organization-templates-table/hooks/useOrganizationTemplatesQuery';
import { OrganizationTemplateChangedBy } from '@/app-shell/settings/organization/type';
import { countMarkdownSections } from '@/helpers/sectionParser';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { trackCommunityTemplatePublished } from '@/lib/analytics/scribe/community';
import { trackGlobalTemplateVisibilityChanged } from '@/lib/analytics/scribe/global-template';
import {
  trackTemplateEdited,
  trackTemplateUpdated,
} from '@/lib/analytics/scribe/template';
import { toast } from '@heidi/design-system/toast';
import { useAreFeatureFlagsResolved } from '@heidi/runtime/feature-flags';
import { useTranslations } from '@heidi/runtime/i18n';
import { TemplateVisibility } from '@heidi/schema/ml-scribe';
import {
  isRegulatoryGuidelineWarningModalV2OpenAtom,
  regulatoryGuidelineBlockModalOpenAtom,
  regulatoryGuidelineComplianceReasonAtom,
  regulatoryGuidelineWarningModalOpenAtom,
} from '@heidi/templates';
import {
  getUpdateTemplateSavePlan,
  useMutateUpdateTemplate as useMutateUpdateTemplateCore,
  willTemplateSaveCreate,
} from '@heidi/templates/data';
import { useIsTemplateComplianceFixForMeEnabled } from '@heidi/templates/flags';
import { useSetAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { useIntl } from 'react-intl';
import { handleTemplateComplianceError } from './handleTemplateComplianceError';

export { mergeTemplateIntoList } from '@heidi/templates/data';

export function useMutateUpdateTemplate({
  showToast = true,
}: { showToast?: boolean } = {}) {
  const { t } = useTranslations();
  const intl = useIntl();
  const { isOrganizationTemplateManager } = useCombinedOrganizationInfo();
  const isFixForMeEnabled = useIsTemplateComplianceFixForMeEnabled();
  const areFlagsResolved = useAreFeatureFlagsResolved();
  const setIsRegulatoryGuidelineBlockModalOpen = useSetAtom(
    regulatoryGuidelineBlockModalOpenAtom
  );
  const setIsRegulatoryGuidelineWarningModalOpen = useSetAtom(
    regulatoryGuidelineWarningModalOpenAtom
  );
  const setIsRegulatoryGuidelineWarningModalV2Open = useSetAtom(
    isRegulatoryGuidelineWarningModalV2OpenAtom
  );
  const setRegulatoryGuidelineComplianceReason = useSetAtom(
    regulatoryGuidelineComplianceReasonAtom
  );
  const { filters } = useFilters();
  const { templates } = useOrganizationTemplatesQuery(filters);

  type PreviousTemplate = NonNullable<typeof templates>['items'][number];

  return useMutateUpdateTemplateCore<OrganizationTemplateChangedBy, PreviousTemplate>({
    isOrganizationTemplateManager,
    getPreviousTemplate: (templateId) =>
      templates?.items.find((t) => t.id === templateId),
    onBeforeCacheSync: (template, variables, previousTemplate) => {
      if (previousTemplate) {
        const didTemplateManagerChangeVisibility =
          isOrganizationTemplateManager &&
          (previousTemplate.template_visibility !== template.template_visibility ||
            !isEqual(previousTemplate.shared_team_ids, template.shared_team_ids));

        if (didTemplateManagerChangeVisibility) {
          trackGlobalTemplateVisibilityChanged({
            templateId: template.id,
            changedBy: variables?.changedBy || OrganizationTemplateChangedBy.EDITOR,
            previousVisibility: previousTemplate.template_visibility,
            visibility: template.template_visibility,
            sharedTeams: template.shared_team_ids || undefined,
          });
        }
      }
    },
    onUpdated: (template, variables) => {
      if (template.id) {
        trackTemplateEdited({
          templateId: template.id,
          templateVisibility: template.template_visibility || TemplateVisibility.User,
        });
        trackTemplateUpdated({
          templateId: template.id,
          templateName: template.name,
          teamId: template.team_id ?? undefined,
          version: IS_TEMPLATE_CREATION_RECORD_ENABLED() ? 'v2' : 'v1',
          sectionCount: countMarkdownSections(variables?.template?.structure_template),
          templateType: variables?.template?.template_category ?? undefined,
          useSections: variables?.template?.use_sections ?? undefined,
          // Analytics only. A non-private save writes a copy, which the create flag gates.
          // The backend returns before the checker when the body has no structure or html.
          complianceCheckEnabled:
            !variables?.saveAnyway &&
            Boolean(
              variables?.template?.structure_template ||
              variables?.template?.template_html
            ) &&
            (willTemplateSaveCreate(
              getUpdateTemplateSavePlan(variables?.template?.is_private)
            )
              ? IS_COMPLIANCE_CHECK_CREATE_TEMPLATE_ENABLED()
              : IS_COMPLIANCE_CHECK_UPDATE_TEMPLATE_ENABLED()),
        });
      }

      if (
        template.template_visibility === TemplateVisibility.Community &&
        variables?.template?.template_visibility !== TemplateVisibility.Community
      ) {
        trackCommunityTemplatePublished({
          templateId: template.id,
        });
      }

      if (showToast) {
        toast({
          tone: 'success',
          title: t('templateEditor.templateUpdatedSuccess'),
        });
      }
    },
    onUpdateFailed: (error, variables) => {
      handleTemplateComplianceError({
        error,
        templateContent:
          variables.template?.structure_template ||
          variables.template?.template_html ||
          '',
        templateSource: 'update',
        templateId: variables.template?.id,
        formatMessage: intl.formatMessage,
        toast,
        setIsRegulatoryGuidelineBlockModalOpen,
        setIsRegulatoryGuidelineWarningModalOpen,
        setIsRegulatoryGuidelineWarningModalV2Open,
        setRegulatoryGuidelineComplianceReason,
        // Before the flag provider is ready the read is the default, not the user's value.
        isFixForMeEnabled: areFlagsResolved ? isFixForMeEnabled : undefined,
      });
    },
  });
}
